import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  Users,
  Mail,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Loader2,
  UserCog,
} from "lucide-react";
import {
  getAdminUsers,
  addAdminUser,
  removeAdminUser,
  updateAdminRole,
  getSiteSettings,
  updateSiteSettings,
} from "@/lib/admin";

export default function AdminSettings() {
  const [adminUsers, setAdminUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    requireAdminApproval: false,
    enableAutoKYC: true,
    maxCardsPerUser: 5,
    maintenanceMode: false,
    debugMode: false,
    emailNotifications: true,
  });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAdminUsers();
    loadSettings();
  }, []);

  const loadAdminUsers = async () => {
    const result = await getAdminUsers();
    if (result.success) {
      setAdminUsers(result.data || []);
    }
    setLoading(false);
  };

  const loadSettings = async () => {
    // Load all settings (no category filter to get both security and system settings)
    const result = await getSiteSettings();
    if (result.success && result.data) {
      setSettings((prev) => ({
        ...prev,
        requireAdminApproval: result.data.require_admin_approval === true || result.data.require_admin_approval === 'true',
        enableAutoKYC: result.data.enable_auto_kyc !== false && result.data.enable_auto_kyc !== 'false',
        maxCardsPerUser: parseInt(result.data.max_cards_per_user) || 5,
        maintenanceMode: result.data.maintenance_mode === true || result.data.maintenance_mode === 'true',
        debugMode: result.data.debug_mode === true || result.data.debug_mode === 'true',
        emailNotifications: result.data.email_notifications !== false && result.data.email_notifications !== 'false',
      }));
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail) return;

    setError("");
    const result = await addAdminUser(newEmail, "admin");

    if (result.success) {
      await loadAdminUsers();
      setNewEmail("");
      toast.success("Admin has been added");
    } else {
      setError(result.error || "Failed to add admin");
      toast.error(result.error || "Failed to add admin");
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    if (!confirm("Are you sure you want to remove this admin? This action cannot be undone.")) {
      return;
    }

    setError("");
    const result = await removeAdminUser(adminId);
    if (result.success) {
      await loadAdminUsers();
      toast.success("Admin has been removed successfully");
    } else {
      console.error("Failed to remove admin:", result.error);
      setError(result.error || "Failed to remove admin");
      toast.error(result.error || "Failed to remove admin");
    }
  };

  const handleRoleChange = async (adminId, newRole) => {
    setError("");
    const result = await updateAdminRole(adminId, newRole);
    if (result.success) {
      await loadAdminUsers();
      const roleText = newRole === 'super_admin' ? 'Super Admin' : 'Admin';
      toast.success(`Role has been changed to ${roleText}`);
    } else {
      setError(result.error || "Failed to update admin role");
      toast.error(result.error || "Failed to change role");
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    const settingsToSave = {
      require_admin_approval: settings.requireAdminApproval,
      enable_auto_kyc: settings.enableAutoKYC,
      max_cards_per_user: settings.maxCardsPerUser,
      maintenance_mode: settings.maintenanceMode,
      debug_mode: settings.debugMode,
      email_notifications: settings.emailNotifications,
    };

    const result = await updateSiteSettings(settingsToSave);

    if (result.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success("Settings have been saved");
    } else {
      setError(result.error || "Failed to save settings");
      toast.error(result.error || "Failed to save settings");
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold">Admin Settings</h2>
        <p className="text-muted-foreground">
          Manage administrator access and permissions
        </p>
      </div>

      {/* Admin Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Administrator Accounts
          </CardTitle>
          <CardDescription>
            Users with these email addresses will have admin access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Admins */}
          <div className="space-y-2">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {error}
              </div>
            )}
            {adminUsers.map((admin) => (
              <div
                key={admin.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{admin.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={admin.role}
                    onValueChange={(value) => handleRoleChange(admin.id, value)}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <UserCog className="h-3 w-3" />
                          Admin
                        </div>
                      </SelectItem>
                      <SelectItem value="super_admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3 w-3" />
                          Super Admin
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {admin.role !== "super_admin" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAdmin(admin.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add New Admin */}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter admin email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="px-4"
              onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
            />
            <Button onClick={handleAddEmail}>
              <Plus className="h-4 w-4 mr-2" />
              Add Admin
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="admin-approval">Require Admin Approval</Label>
                <p className="text-sm text-muted-foreground">
                  New users must be approved by an admin before accessing the
                  platform
                </p>
              </div>
              <Switch
                id="admin-approval"
                checked={settings.requireAdminApproval}
                onCheckedChange={(checked) =>
                  handleSettingChange("requireAdminApproval", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-kyc">Enable Auto KYC</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically initiate KYC verification for new users
                </p>
              </div>
              <Switch
                id="auto-kyc"
                checked={settings.enableAutoKYC}
                onCheckedChange={(checked) =>
                  handleSettingChange("enableAutoKYC", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="max-cards">Maximum Cards per User</Label>
                <p className="text-sm text-muted-foreground">
                  Limit the number of cards each user can create
                </p>
              </div>
              <Input
                id="max-cards"
                type="number"
                min="1"
                max="10"
                value={settings.maxCardsPerUser}
                onChange={(e) =>
                  handleSettingChange(
                    "maxCardsPerUser",
                    parseInt(e.target.value)
                  )
                }
                className="w-20"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            System Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="maintenance">Maintenance Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Temporarily disable user access for maintenance
                </p>
              </div>
              <Switch
                id="maintenance"
                checked={settings.maintenanceMode}
                onCheckedChange={(checked) =>
                  handleSettingChange("maintenanceMode", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="debug">Debug Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Enable detailed logging for troubleshooting
                </p>
              </div>
              <Switch
                id="debug"
                checked={settings.debugMode}
                onCheckedChange={(checked) =>
                  handleSettingChange("debugMode", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="notifications">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Send email alerts for important admin events
                </p>
              </div>
              <Switch
                id="notifications"
                checked={settings.emailNotifications}
                onCheckedChange={(checked) =>
                  handleSettingChange("emailNotifications", checked)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {saved ? "Settings Saved!" : "Save Settings"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
